/**
 * TF-IDF KSA-overlap source builder. Replaces the unweighted Jaccard approach
 * in the previous ksa-overlap.util.ts.
 *
 * Why this exists: Jaccard treats every KSA equally. The "important-in-banking"
 * KSA universe is dominated by generic-professional KSAs (Oral Comprehension,
 * Active Listening, Working with Computers, English Language, etc.) that
 * appear in 73-87% of all 45 banking jobs. Under Jaccard, broad-KSA user
 * occupations like Home Health Aide (27 GREATs at 0.30 threshold) and
 * Substitute Teacher (16 GREATs) clear the bar based on these generic
 * overlaps, not on any real banking-specific alignment. TF-IDF cosine fixes
 * this by weighting universal-in-corpus KSAs near zero and banking-
 * discriminating KSAs proportionally higher.
 *
 * Formula choices (Phase 0 measured against live CPB data):
 *
 *   IMPORTANCE_THRESHOLD = 3.5
 *     Same as the predecessor. Filters to important KSAs per occupation.
 *
 *   IDF = log((N + 1) / (df + 1))    [no trailing +1 offset]
 *     Sklearn's default smoothed IDF adds a +1 to keep IDF strictly positive,
 *     but for our product question ("which banking jobs match?") universal-in-
 *     corpus KSAs should contribute zero — they convey no information about
 *     which banking job is a fit. Dropping the +1 takes Home Health Aide from
 *     ~10 spurious GREATs (with +1) to 1 (without).
 *
 *   USER VECTOR restricted to banking-corpus KSAs
 *     A Waiter has food-service-specific KSAs that aren't in any banking job.
 *     Including them in the user's L2 normalization dilutes the banking-
 *     relevant signal. Restricting concentrates the user's vector mass on
 *     KSAs that can actually contribute to a banking cosine.
 *
 *   COSINE_THRESHOLD = 0.35
 *     Sits at the natural noise-floor cliff visible across all 8 measured
 *     personas. For Waiter: 0.598, 0.519, 0.421, ... 0.354 -- cliff -- 0.244.
 *     For Home Health Aide: 0.378 -- cliff -- 0.298.
 *     0.30 admits noise; 0.40+ erases the realistic mid-tier cases.
 *
 * Binary tier output (greatFit only): KSA-overlap's job is specifically to
 * promote credential-aligned users to GREAT on banking jobs whose KSAs they
 * meaningfully share. Weak overlap is not surfaced as goodFit/okFit — the
 * other sources (jobFamilyOccupations, degreeProgramOccupations) cover those
 * tiers.
 */

const IMPORTANCE_THRESHOLD_DEFAULT = 3.5;
const COSINE_THRESHOLD_DEFAULT = 0.35;

interface OccupationKSA {
  ksa: { id: string };
  importance?: number | null;
}

interface UserOccupation {
  onetCode: string;
  occupationKSAs?: OccupationKSA[] | null;
}

export interface TfIdfOverlapSource {
  greatFit: { onetCode: string }[];
  goodFit: never[];
  okFit: never[];
}

/**
 * Returns the set of KSA ids whose importance is at or above the threshold.
 * Defensive against missing / null importance values (treated as zero).
 */
export const importantKsaSet = (
  occupationKSAs: OccupationKSA[] | null | undefined,
  threshold: number = IMPORTANCE_THRESHOLD_DEFAULT
): Set<string> => {
  const result = new Set<string>();
  if (!occupationKSAs?.length) {
    return result;
  }
  for (const k of occupationKSAs) {
    const imp = k?.importance ?? 0;
    if (imp >= threshold) {
      result.add(k.ksa.id);
    }
  }
  return result;
};

/**
 * Computes the inverse document frequency for every KSA appearing in any
 * banking-corpus job's important-KSA set. KSAs with df = 0 (absent from the
 * corpus) are not returned. Universal KSAs (df = N) return idf = 0.
 */
export const computeBankingIdf = (
  bankingKsasByOnet: Record<string, OccupationKSA[]>,
  importanceThreshold: number = IMPORTANCE_THRESHOLD_DEFAULT
): Record<string, number> => {
  const onets = Object.keys(bankingKsasByOnet);
  const N = onets.length;
  if (N === 0) {
    return {};
  }
  const df = new Map<string, number>();
  for (const onet of onets) {
    const set = importantKsaSet(bankingKsasByOnet[onet], importanceThreshold);
    for (const kid of set) {
      df.set(kid, (df.get(kid) ?? 0) + 1);
    }
  }
  const result: Record<string, number> = {};
  for (const [kid, count] of df) {
    result[kid] = Math.log((N + 1) / (count + 1));
  }
  return result;
};

/**
 * Builds a sparse L2-normalized TF-IDF vector for a single occupation. Binary
 * TF: each KSA in `importantKsas` contributes its IDF weight; the vector is
 * then normalized to unit length. KSAs not present in `idfMap` are dropped
 * (we have no weight information for them). KSAs with zero IDF (universal in
 * the corpus) are dropped from the returned map for sparseness; including
 * them with weight 0 has no effect on cosine.
 *
 * `restrictToCorpus` is kept as a parameter for API symmetry but is
 * functionally equivalent to the default behavior — KSAs missing from the
 * idfMap are always dropped, because we have no weight for them.
 */
export const buildTfIdfVector = (
  importantKsas: Set<string>,
  idfMap: Record<string, number>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _restrictToCorpus: boolean
): Map<string, number> => {
  const rawWeights: Array<[string, number]> = [];
  for (const kid of importantKsas) {
    const idf = idfMap[kid];
    if (idf === undefined || idf <= 0) {
      continue;
    }
    rawWeights.push([kid, idf]);
  }
  if (rawWeights.length === 0) {
    return new Map();
  }
  let magSq = 0;
  for (const [, w] of rawWeights) {
    magSq += w * w;
  }
  const mag = Math.sqrt(magSq);
  if (mag === 0) {
    return new Map();
  }
  const vec = new Map<string, number>();
  for (const [kid, w] of rawWeights) {
    vec.set(kid, w / mag);
  }
  return vec;
};

/**
 * Cosine similarity between two sparse vectors. Since we pass L2-normalized
 * vectors, this is equivalent to a dot product. Returns 0 on empty inputs.
 */
export const cosineSimilarity = (a: Map<string, number>, b: Map<string, number>): number => {
  if (a.size === 0 || b.size === 0) {
    return 0;
  }
  // Iterate the smaller map for efficiency.
  const [smaller, larger] = a.size <= b.size ? [a, b] : [b, a];
  let dot = 0;
  for (const [kid, w] of smaller) {
    const other = larger.get(kid);
    if (other !== undefined) {
      dot += w * other;
    }
  }
  return dot;
};

/**
 * Builds a synthetic skill-aggregation source from TF-IDF cosine over the
 * banking corpus. For each banking job, compute the MAX cosine across all
 * user occupations. If the max is at or above `cosineThreshold`, the banking
 * job's onet code is pushed into `greatFit`. `goodFit` and `okFit` are
 * always empty -- KSA-overlap is binary by design.
 */
export const buildTfIdfOverlapSource = (
  userOccupations: UserOccupation[] | null | undefined,
  bankingKsasByOnet: Record<string, OccupationKSA[]> | null | undefined,
  cosineThreshold: number = COSINE_THRESHOLD_DEFAULT,
  importanceThreshold: number = IMPORTANCE_THRESHOLD_DEFAULT
): TfIdfOverlapSource => {
  const empty: TfIdfOverlapSource = { greatFit: [], goodFit: [], okFit: [] };
  if (!userOccupations?.length) {
    return empty;
  }
  if (!bankingKsasByOnet || Object.keys(bankingKsasByOnet).length === 0) {
    return empty;
  }

  const idfMap = computeBankingIdf(bankingKsasByOnet, importanceThreshold);

  // Pre-compute user vectors (restricted to corpus KSAs).
  const userVecs = userOccupations.map(occ =>
    buildTfIdfVector(importantKsaSet(occ.occupationKSAs, importanceThreshold), idfMap, true)
  );

  const greatFit: { onetCode: string }[] = [];
  for (const [bankingOnet, bankingKsas] of Object.entries(bankingKsasByOnet)) {
    const bankingVec = buildTfIdfVector(
      importantKsaSet(bankingKsas, importanceThreshold),
      idfMap,
      false
    );
    if (bankingVec.size === 0) {
      continue;
    }
    let maxCosine = 0;
    for (const userVec of userVecs) {
      if (userVec.size === 0) {
        continue;
      }
      const sim = cosineSimilarity(userVec, bankingVec);
      if (sim > maxCosine) {
        maxCosine = sim;
      }
      if (maxCosine >= 1) {
        break; // perfect match short-circuit
      }
    }
    if (maxCosine >= cosineThreshold) {
      greatFit.push({ onetCode: bankingOnet });
    }
  }

  return { greatFit, goodFit: [], okFit: [] };
};
